import { useCallback, useMemo } from "react";
import { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { SubScopeProvider } from "@state/subScopeContext";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import { useUpdateOrganizationTaskTemplateMutation } from "@api/organizationTaskTemplate.api";
import {
    EditCloseTemplateTaskDrawer,
    type EditTaskResult,
} from "../OrganizationCloseTemplates/EditCloseTemplateTaskDrawer";
import { toLocalTask, toUpdateBody } from "./organizationTaskTemplateMappers";

interface Props {
    template: OrganizationTaskTemplate | null;
    onClose: () => void;
}

export const EditOrganizationTaskTemplateDrawer = ({
    template,
    onClose,
}: Props) => {
    const sortedStages = useSortedCloseStages();
    const { toast: successToast, error: errorToast } = useToaster();
    const { mutateAsync } = useUpdateOrganizationTaskTemplateMutation();

    const task = useMemo(
        () => (template ? toLocalTask(template) : null),
        [template],
    );

    const handleSave = useCallback(
        async (templateId: string, result: EditTaskResult) => {
            try {
                await mutateAsync({ templateId, body: toUpdateBody(result) });
            } catch (error) {
                errorToast(`Failed to update task template "${result.name}"`);
                throw error;
            }
            successToast(`Updated task template "${result.name}"`);
            onClose();
        },
        [mutateAsync, onClose, successToast, errorToast],
    );

    return (
        <SubScopeProvider
            value={{ workspaceId: template?.workspaceId ?? undefined }}
        >
            <EditCloseTemplateTaskDrawer
                key={task?.localId ?? "none"}
                open={task !== null}
                task={task}
                sortedStages={sortedStages}
                onClose={onClose}
                onSave={handleSave}
                basis={null}
            />
        </SubScopeProvider>
    );
};
